/**
 * Collects the alt instances that LINK / LINK_TO fields already point at, so that any repair of "unlinked" fields
 * reuses them instead of minting new, empty alt instances.
 *
 * Used by verifyFormGroupInstances (src/hooks/useForms.tsx) before it walks the fields. Both top-level fields and table
 * cells are considered. When several alts exist for the same link id, the one that actually holds a value wins, then the
 * first one seen.
 */

export interface LinkedFieldLike {
	altInstanceId?: string | null;
	altInstance?: { value?: string | null } | null;
	fieldTemplate?: {
		action?: string[] | null;
		actionData?: string[] | null;
	} | null;
	tableFields?: { items?: LinkedFieldLike[] | null } | null;
}

export interface LinkedFormInstanceLike {
	fieldInstances?: { items?: LinkedFieldLike[] | null } | null;
}

/**
 * Returns the link id (actionData paired with a LINK or LINK_TO action) for a field, or undefined when it has none.
 */
export function getLinkActionId(field: LinkedFieldLike): string | undefined {
	const actions = field.fieldTemplate?.action || [];
	const actionData = field.fieldTemplate?.actionData || [];
	let linkId: string | undefined;
	for (let i = 0; i < actions.length; i++) {
		if (actions[i] === "LINK" || actions[i] === "LINK_TO") {
			linkId = actionData[i] || undefined;
		}
	}
	return linkId;
}

/**
 * @param formInstances The form instances of a form group, with their field instances (and table cells) attached
 * @returns A map of link id → alt instance id that should be used for that link
 */
export function seedLinkedAltIds(formInstances: LinkedFormInstanceLike[]): Record<string, string> {
	const linked: Record<string, string> = {};
	const linkedHasValue: Record<string, boolean> = {};

	function consider(field: LinkedFieldLike) {
		if (!field.altInstanceId) return;
		const linkId = getLinkActionId(field);
		if (!linkId) return;
		const hasValue = !!field.altInstance?.value;
		if (!linked[linkId] || (hasValue && !linkedHasValue[linkId])) {
			linked[linkId] = field.altInstanceId;
			linkedHasValue[linkId] = hasValue;
		}
	}

	for (const formInstance of formInstances) {
		for (const field of formInstance.fieldInstances?.items || []) {
			consider(field);
			for (const cell of field.tableFields?.items || []) {
				consider(cell);
			}
		}
	}
	return linked;
}

export default seedLinkedAltIds;
